import { openDB } from 'idb';
import { AbstractTransport } from './abstract-transport';
import { logsComparator, preventUnhandledRejection, serializeMsgPartsMemoized } from '../utils';
import { sum } from '@ringcentral/rwc-utils/src/array';
import '../types';

// prettier-ignore
const errors = {
    UNAVAILABLE: 'IndexedDB is unavailable',
    ALREADY_CREATED: 'The IndexedDBTransport singleton is already created, current options haven\'t been applied',
    ALREADY_REJECTED: 'rejected',
    DATA_CLONE_ERR: 'DataCloneError'
};
const DELETE_DEBOUNCE_WAIT = 500;
const TIMESTAMP_INDEX_NAME = 'tsz';
const TIMESTAMP_KEY_PATH = 'tsz';

const idbKey = Symbol.for('indexed-db-transport');

export class IndexedDBTransport extends AbstractTransport {
    /**
     * returns singletone, instance of which saved in global object.
     * It should be saved in global object, not just local variable, to make it work in case with several independent .js modules on page
     * (for instance, when we use it from 'error-catch.js' and 'appjs.js' simultaneously)
     *
     * @param {{ storeNames: string[]=}} options
     * @returns { IndexedDBTransport }
     */
    static getInstance(options) {
        if (!globalThis[idbKey]) {
            globalThis[idbKey] = new IndexedDBTransport(options);
        } else {
            // eslint-disable-next-line no-console
            console.warn(errors.ALREADY_CREATED);
        }

        return globalThis[idbKey];
    }

    version = 6;

    tsIndexName = TIMESTAMP_INDEX_NAME;

    constructor({
        dbName = 'rwcLogs',
        storeNames = [],
        entryLifespan = 1000 * 60 * 60 * 24 * 7,
        entriesLimitPerStorage = 20000,
        ...opts
    }) {
        super(opts);
        this.dbName = dbName;
        this.storeNames = storeNames;

        this.applyRuntimeOpts({
            entryLifespan,
            entriesLimitPerStorage,
        });

        // not used at the moment because getStorageUsage is unreliable
        this.approxEntrySize = 1000;
        this.maxStorageUsage = 2000000;

        this.databasePromise = this.initialize();
        // for debounce idb delete operation
        this.prevTime = 0;
    }

    /** @private */
    async initialize() {
        return openDB(this.dbName, this.version, {
            upgrade: this.upgradeDatabase,
            blocked: e => {
                this.databasePromise = Promise.reject(e);
            },
        });
    }

    /** @private */
    // eslint-disable-next-line no-unused-vars
    upgradeDatabase = (database, oldVersion, newVersion, transaction) => {
        this.storeNames
            .filter(val => !database.objectStoreNames.contains(val))
            .forEach(val => {
                const obj = database.createObjectStore(val, { autoIncrement: true });
                obj.createIndex(TIMESTAMP_INDEX_NAME, TIMESTAMP_KEY_PATH, { unique: false });
                return obj;
            });

        for (let i = 0; i < database.objectStoreNames.length; i++) {
            const storeName = database.objectStoreNames[i];

            if (!this.storeNames.includes(storeName)) {
                database.deleteObjectStore(storeName);
                continue;
            }

            const store = transaction.objectStore(storeName);

            if (!store.indexNames.contains(TIMESTAMP_INDEX_NAME)) {
                store.createIndex(TIMESTAMP_INDEX_NAME, TIMESTAMP_KEY_PATH, { unique: false });
            }
        }
    };

    /**
     * @param {LoggerFormattedMessage} message - message formatted by logger
     * @returns {Promise}
     */
    log(message) {
        const { lvl, ...item } = message;
        const serializedParts = serializeMsgPartsMemoized(message.parts);
        let dataBase;

        return this.databasePromise
            .catch(() => Promise.reject(errors.ALREADY_REJECTED))
            .then(() => this.debounceDeleteOperation(lvl))
            .then(() => this.databasePromise)
            .then(db => {
                item.parts = serializedParts;
                dataBase = db;
                return db.put(lvl, item);
            })
            .catch(err => {
                if (err.name === errors.DATA_CLONE_ERR) {
                    const strParts = serializedParts.map(part => {
                        let constructorName;
                        try {
                            constructorName = part?.constructor?.name || '';

                            return {
                                p: JSON.stringify(part),
                                i: `${typeof part} ${constructorName} `,
                            };
                        } catch (e) {
                            console.error('Failed to serialize part:', part, '\nerror:', e); // eslint-disable-line no-console

                            return `unknwn: ${typeof part} ${constructorName ?? ''};Err: ${e}`;
                        }
                    });
                    return dataBase.put(lvl, { ...item, parts: strParts });
                } else {
                    throw err;
                }
            })
            .catch(async err => {
                // prevents emitting of unhandled rejection when the transport disabled
                if (err === errors.ALREADY_REJECTED) return;

                /* eslint-disable no-console */
                console.error('Failed to save message to IDB:', err, '\nmessage:', message);

                const errPromise = Promise.reject(err);
                preventUnhandledRejection(errPromise);

                if (err.name !== errors.DATA_CLONE_ERR) {
                    console.error('IDB transport disabled');
                    this.databasePromise = errPromise;
                }
                /* eslint-enable no-console */
            });
    }

    /**
     * @private
     * @param   { string } objectStoreName
     * @returns { Promise }
     */
    async debounceDeleteOperation(objectStoreName) {
        const nowTime = Date.now();
        const shouldDelete = nowTime - this.prevTime > DELETE_DEBOUNCE_WAIT;

        this.prevTime = nowTime;

        if (shouldDelete) {
            await this.deleteStaleEntries(objectStoreName);
            await this.retainEntriesAmountLimit(objectStoreName);
        }
    }

    /**
     * @private
     * @param   { string } objectStoreName
     * @returns { Promise }
     */
    async deleteStaleEntries(objectStoreName) {
        const db = await this.databasePromise;
        const tx = db.transaction(objectStoreName, 'readwrite');
        let cursor = await tx.store.openCursor();
        while (cursor) {
            if (this.isEntryStale(cursor.value)) {
                tx.store.delete(cursor.key);
                cursor = await cursor.continue();
            } else {
                break;
            }
        }

        await tx.done;
    }

    isEntryStale = entry =>
        new Date().getTime() > new Date(entry.tsz).getTime() + this.getRuntimeOpts().entryLifespan;

    /**
     * @private
     * @param   { string } objectStoreName
     * @returns { Promise }
     */
    async retainEntriesAmountLimit(objectStoreName) {
        const db = await this.databasePromise;
        const entriesCount = await db.count(objectStoreName);
        const { entriesLimitPerStorage } = this.getRuntimeOpts();
        const limitExceedsBy = entriesCount - entriesLimitPerStorage;
        return limitExceedsBy > 0
            ? this.deleteEntriesFromStore(
                  objectStoreName,
                  Math.ceil(limitExceedsBy + entriesLimitPerStorage / 100)
              )
            : Promise.resolve();
    }

    /**
     * @private
     * @param   { string }    objectStoreName
     * @param   { number }          count
     * @returns { Promise }
     */
    async deleteEntriesFromStore(objectStoreName, count) {
        const db = await this.databasePromise;
        const tx = db.transaction(objectStoreName, 'readwrite');
        let cursor = await tx.store.openCursor();
        while (count > 0 && cursor) {
            tx.store.delete(cursor.key);
            cursor = await cursor.continue();
            count--;
        }

        await tx.done;
    }

    async getTotalEntriesCount() {
        const db = await this.databasePromise;

        const promises = this.storeNames.map(storeName => db.count(storeName));

        return Promise.all(promises).then(sum);
    }

    getKeysByStore() {
        return this.databasePromise.then(db => {
            const promises = this.storeNames.map(storeName =>
                db.getAllKeys(storeName).then(keys => [storeName, keys])
            );

            return Promise.all(promises).then(Object.fromEntries);
        });
    }

    // navigator.storage.estimate is unreliable, so method should be improved or removed
    getStorageUsage() {
        return navigator.storage && navigator.storage.estimate
            ? navigator.storage.estimate().then(estimatedStorage => estimatedStorage.usage)
            : this.getTotalEntriesCount().then(count => count * this.approxEntrySize);
    }

    /**
     * @private
     * @param   { number }  entriesNumberToDelete
     * @returns { Promise }
     */
    async deleteEntriesEvenly(entriesNumberToDelete) {
        const db = await this.databasePromise;
        const keysByStore = await this.getKeysByStore();

        const logsTotalCount = sum(this.storeNames.map(store => keysByStore[store].length));

        entriesNumberToDelete = Math.min(logsTotalCount, entriesNumberToDelete);
        if (entriesNumberToDelete === 0) {
            return Promise.resolve();
        }

        const deleteTransactions = [];

        let counter = 0;

        while (entriesNumberToDelete > 0) {
            const storeName = this.storeNames[counter % this.storeNames.length];
            if (keysByStore[storeName].length) {
                const gen = Math.floor(counter / this.storeNames.length);
                const deleteTransaction = db.delete(storeName, keysByStore[storeName][gen]);
                deleteTransactions.push(deleteTransaction);
                entriesNumberToDelete--;
            }

            counter++;
        }

        return Promise.all(deleteTransactions);
    }

    /**
     * Returns logs for specified stores and period
     * @param {string[]} [storeNames]
     * @param {Date|undefined} [startTime] - from Date
     * @param {Date|undefined} [endTime] - to Date
     * @return {Promise.<Object.<string, LoggerFormattedMessage[]>>} records by store name
     */
    async getLogs(storeNames = this.storeNames, startTime, endTime) {
        const db = await this.databasePromise;
        const entries = await Promise.all(
            storeNames.map(async storeName => {
                const tx = db.transaction(storeName);
                const store = tx.objectStore(storeName);
                const index = store.index(TIMESTAMP_INDEX_NAME);

                let range;
                if (startTime !== undefined && endTime !== undefined) {
                    range = IDBKeyRange.bound(startTime.toISOString(), endTime.toISOString());
                } else if (startTime !== undefined) {
                    range = IDBKeyRange.lowerBound(startTime.toISOString());
                } else if (endTime !== undefined) {
                    range = IDBKeyRange.upperBound(endTime.toISOString());
                }

                const records = await index.getAll(range);

                await tx.done;

                for (const record of records) {
                    record.lvl = storeName;
                }

                return records;
            })
        );

        return entries.flat().sort(logsComparator);
    }

    /**
     * Deletes logs for specified stores
     * @param {string[]} [storeNames]
     * @return {Promise}
     */
    async clearLogs(storeNames = this.storeNames) {
        const db = await this.databasePromise;
        return Promise.all(
            storeNames.map(async storeName => {
                const tx = db.transaction(storeName, 'readwrite');
                await tx.objectStore(storeName).clear();
                await tx.done;
            })
        );
    }
}
